import { ref } from 'vue'
import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js'
import { bucketAmount } from '@aurumrun/analytics'
import { trackSupabaseChannelStatus } from '~/utils/reconnectTelemetry'
import { tokenLabel } from '~/utils/tokenLabel'

/**
 * CLIENT-ONLY COMPOSABLE
 *
 * Module-level singleton — one channel per session, shared across all
 * component instances that call useDepositRealtime().
 *
 * Listens for deposit:created broadcast events sent by alchemy-webhook
 * via httpBroadcast. Uses the broadcast pattern (not postgres_changes)
 * because RLS policies on the deposits table are service_role only
 */
let depositChannel: RealtimeChannel | null = null
let currentUserId: string | null = null
const isSubscribed = ref(false)

// Guard against a duplicate success toast when both the credit broadcast and a
// reconcile/backfill credit emit deposit:completed for the same deposit.
const completedDepositIds = new Set<string>()

// Dedup a repeated failure push (a Bitolo webhook and the backfill can both
// emit deposit:failed for the same deposit).
const failedDepositIds = new Set<string>()

interface DepositEvent {
  id: string
  input_amount: number | null
  input_token: string | null
  chain: string | null
}

const STABLE_TOKENS = new Set(['USDC', 'USDT'])
const GOLD_TOKENS = new Set(['XAUT', 'PAXG'])

const CHAIN_LABELS: Record<string, string> = {
  ethereum: 'Ethereum',
  arbitrum: 'Arbitrum',
  base: 'Base',
  polygon: 'Polygon',
  optimism: 'Optimism',
  bsc: 'BNB Chain',
  avalanche: 'Avalanche',
  solana: 'Solana',
  tron: 'Tron',
  ton: 'TON',
}

function formatTokenAmount(amount: number, symbol: string): string {
  if (!Number.isFinite(amount) || amount === 0) return '0'
  const upper = symbol.toUpperCase()
  const maxDigits = STABLE_TOKENS.has(upper) ? 2 : GOLD_TOKENS.has(upper) ? 4 : 6
  const threshold = Math.pow(10, -maxDigits)
  if (Math.abs(amount) < threshold) return `< ${threshold.toFixed(maxDigits)}`
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: maxDigits,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatChainLabel(chain: string): string {
  const key = chain.toLowerCase()
  return CHAIN_LABELS[key] || chain.charAt(0).toUpperCase() + chain.slice(1)
}

export function useDepositRealtime() {
  const nuxtApp = useNuxtApp()
  const supabase = nuxtApp.$supabase as SupabaseClient
  // Global translator: works in the detached session-restore path, where
  // useI18n() throws because there is no active component instance.
  const { t } = nuxtApp.$i18n
  const toast = useToast()

  function subscribe(userId: string): void {
    if (depositChannel && currentUserId === userId) return

    if (depositChannel) unsubscribe()

    currentUserId = userId

    // private:true gates this user-scoped topic via messages_owner_topics_select
    // RLS so only the owner can subscribe (closes M-3 impersonation, §5.12).
    depositChannel = supabase
      .channel(`deposit-watch-${userId}`, { config: { private: true } })
      .on('broadcast', { event: 'deposit:created' }, ({ payload }) => {
        const data = payload as DepositEvent
        // Intent-only rows (e.g. ChangeNow widget opened, no transfer yet) broadcast
        // with input_amount=null. Refresh wallet history but don't toast — nothing
        // has actually been received.
        if (data.input_amount == null) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('deposit:received'))
          }
          return
        }
        const token = (data.input_token || 'crypto').toUpperCase()
        const chain = formatChainLabel(data.chain || 'unknown')
        const amount = formatTokenAmount(data.input_amount, token)
        toast.success(t('errors.deposit.receivedTitle'), t('errors.deposit.receivedBody', { amount, token: tokenLabel(token), chain }))

        useAnalytics().track('deposit_completed', {
          chain: data.chain ?? 'unknown',
          amountBucket: bucketAmount(data.input_amount),
        })

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('deposit:received'))
        }
      })
      .on('broadcast', { event: 'deposit:completed' }, ({ payload }) => {
        const depositId = (payload as { depositId?: string } | undefined)?.depositId
        if (depositId) {
          if (completedDepositIds.has(depositId)) return
          completedDepositIds.add(depositId)
        }
        toast.success(t('errors.deposit.completedTitle'), t('errors.deposit.completedBody'))

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('deposit:received'))
        }
      })
      .on('broadcast', { event: 'deposit:failed' }, ({ payload }) => {
        const depositId = (payload as { depositId?: string } | undefined)?.depositId
        if (depositId) {
          if (failedDepositIds.has(depositId)) return
          failedDepositIds.add(depositId)
        }
        toast.error(t('errors.deposit.failedTitle'), t('errors.deposit.failedBody'))

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('deposit:received'))
        }
      })
      .on('broadcast', { event: 'deposit:progress' }, ({ payload }) => {
        // No toast: the deposit panel renders this, and one per confirmation
        // transition would be noise for a user who is already watching.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('deposit:progress', { detail: payload }))
        }
      })
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          isSubscribed.value = true
        } else if (status === 'CHANNEL_ERROR') {
          isSubscribed.value = false
          console.error('[DepositRealtime] Channel error:', err?.message || 'Unknown error')
        } else if (status === 'CLOSED') {
          isSubscribed.value = false
        }
        trackSupabaseChannelStatus('deposit', status, err?.message)
      })
  }

  function unsubscribe(): void {
    if (depositChannel) {
      supabase.removeChannel(depositChannel)
      depositChannel = null
      currentUserId = null
      isSubscribed.value = false
      completedDepositIds.clear()
      failedDepositIds.clear()
    }
  }

  return { isSubscribed, subscribe, unsubscribe }
}
